import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Bell,
  BookOpenText,
  Cake,
  CaretDown,
  CaretRight,
  ClipboardText,
  Eye,
  Folders,
  Gear,
  GraduationCap,
  Image,
  Info,
  Lightning,
  Link as LinkIcon,
  Megaphone,
  Newspaper,
  Phone,
  ShieldStar,
  SquaresFour,
  TreeStructure,
  X,
} from "@phosphor-icons/react";
import { NAV } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const AUTHOR_ROLES = ["autor_area", "administrador_total", "admin"];
const ADMIN_ROLES = ["administrador_total", "admin"];
const MANAGER_ROLES = ["gerente", "administrador_total", "admin"];
const FORM_ROLES = ["gerente", "autor_area", "administrador_total", "admin", "area_manager", "coordinator", "regional_manager", "store_manager"];

const navGroups = [
  {
    label: null,
    items: [{ label: "Dashboard", icon: SquaresFour, path: "/dashboard" }]
  },
  {
    label: "Comunicação",
    items: [
      { label: "Boletim", icon: Newspaper, path: "/boletim" },
      { label: "Mural de Avisos", icon: Bell, path: "/mural" },
      { label: "Campanhas", icon: Megaphone, path: "/campanhas" },
      { label: "Última Hora", icon: Lightning, path: "/ultima-hora" },
    ]
  },
  {
    label: "Conhecimento",
    items: [
      { label: "Procedimentos", icon: BookOpenText, path: "/procedimentos" },
      { label: "Documentos", icon: Folders, path: "/documentos" },
      { label: "Treinamentos", icon: GraduationCap, path: "/treinamentos" },
      { label: "Informações Internas", icon: Info, path: "/informacoes" },
    ]
  },
  {
    label: "Operacional",
    items: [
      { label: "Formulários", icon: ClipboardText, path: "/formularios" },
      { label: "Links Úteis", icon: LinkIcon, path: "/links-uteis" },
    ]
  },
  {
    label: "Pessoas",
    items: [
      { label: "Organograma", icon: TreeStructure, path: "/organograma" },
      { label: "Aniversariantes", icon: Cake, path: "/aniversariantes" },
      { label: "Contatos", icon: Phone, path: "/contatos" },
    ]
  },
  {
    label: "Mídia",
    items: [
      { label: "Galeria de Fotos", icon: Image, path: "/galeria" },
    ]
  }
];

function NavItem({ item, onClick }) {
  const location = useLocation();
  const active = location.pathname === item.path;
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      data-testid={`${NAV.sidebarItem}-${item.path.replace("/", "")}`}
      onClick={onClick}
      className={cn(
        "flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2346ff]/25",
        active
          ? "bg-[#eef1ff] text-[#2346ff] shadow-[inset_0_0_0_1px_rgba(35,70,255,0.08)]"
          : "text-slate-600 hover:bg-[#f5f7ff] hover:text-[#2346ff]"
      )}
    >
      <Icon className="h-4 w-4 flex-shrink-0 text-[#2346ff]" weight="regular" />
      <span className="truncate">{item.label}</span>
      {active && <CaretRight className="ml-auto h-3 w-3 opacity-70" weight="regular" />}
    </Link>
  );
}

export default function Sidebar({ onClose }) {
  const { user, hasAnyRole } = useAuth();
  const [collapsed, setCollapsed] = useState({});

  const toggleGroup = (label) => setCollapsed(current => ({ ...current, [label]: !current[label] }));
  const canViewItem = (item) => item.path !== "/formularios" || hasAnyRole(FORM_ROLES);
  const canManageContent = hasAnyRole(AUTHOR_ROLES);
  const canAdminister = hasAnyRole(ADMIN_ROLES);
  const canViewManagerContent = hasAnyRole(MANAGER_ROLES);

  return (
    <div data-testid={NAV.sidebar} className="flex h-full w-[272px] flex-col border-r border-slate-200/80 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5">
        <div className="min-w-0">
          <img src="/schumann-logo.svg" alt="Schumann" className="h-auto w-[170px]" />
          <p className="ml-[42px] mt-1 text-[10px] uppercase tracking-[0.2em] text-slate-400">Intranet</p>
        </div>
        {onClose && (
          <button
            type="button"
            aria-label="Fechar menu"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-xl text-slate-500 transition hover:bg-[#f5f7ff] md:hidden"
          >
            <X className="h-4 w-4 text-[#2346ff]" weight="regular" />
          </button>
        )}
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto px-3 py-3">
        {navGroups.map((group) => (
          <div key={group.label || "main"}>
            {group.label && (
              <button
                type="button"
                onClick={() => toggleGroup(group.label)}
                className="mb-1 mt-2 flex min-h-9 w-full items-center justify-between rounded-lg px-2 py-1 transition hover:bg-slate-50"
              >
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
                  {group.label}
                </span>
                {collapsed[group.label]
                  ? <CaretRight className="h-3 w-3 text-[#2346ff]" weight="regular" />
                  : <CaretDown className="h-3 w-3 text-[#2346ff]" weight="regular" />
                }
              </button>
            )}
            {!collapsed[group.label] && (
              <div className="space-y-0.5">
                {group.items.filter(canViewItem).map(item => (
                  <NavItem key={item.path} item={item} onClick={onClose} />
                ))}
              </div>
            )}
          </div>
        ))}

        {(canManageContent || canViewManagerContent || canAdminister) && (
          <div>
            <p className="mb-1 mt-2 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
              Minha área
            </p>
            {canManageContent && (
              <NavItem item={{ label: "Gestão de Conteúdo", icon: Folders, path: "/gestao-conteudo" }} onClick={onClose} />
            )}
            {canViewManagerContent && (
              <NavItem item={{ label: "Conteudos da Gerencia", icon: Eye, path: "/conteudos-gerencia" }} onClick={onClose} />
            )}
            {canAdminister && (
              <NavItem item={{ label: "Administração Total", icon: ShieldStar, path: "/admin-total" }} onClick={onClose} />
            )}
            {canAdminister && (
              <NavItem item={{ label: "Painel Admin atual", icon: Gear, path: "/admin" }} onClick={onClose} />
            )}
          </div>
        )}
      </div>

      {user && (
        <div className="border-t border-slate-100 px-3 py-3">
          <Link to="/perfil" className="flex items-center gap-2 rounded-xl p-2 transition hover:bg-slate-50">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#2346ff] to-[#1d3ace] text-sm font-bold text-white">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-xl object-cover" />
              ) : (
                (user.name || "U").charAt(0).toUpperCase()
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{user.name}</p>
              <p className="truncate text-xs text-slate-400">{user.department || user.role}</p>
            </div>
          </Link>
        </div>
      )}
    </div>
  );
}
